/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useState } from 'react';

// icons

import {
	Typography,
	Pager,
	Select,
	Button,
	ListTable,
} from 'design_system/src';

import { useNavigate } from 'react-router';
import './devices.scss';

import { ReactComponent as Eye } from 'design_system/src/static/icons/eye.svg';
import { ReactComponent as Edit } from 'design_system/src/static/icons/edit-ui-2.svg';
import { ReactComponent as AddCircle } from 'design_system/src/static/icons/add-circle.svg';

import { getDevicesFilter, setDevicesFilter } from 'localStorage/filterStorage';
import { handleEventGoogle } from 'models/shared/helpers/googleAnalytics';
import { fetchDevices } from 'models/geozon/controllers/device.controller';
import { Device, VehicleItem } from 'models/geozon/interfaces/device.interface';
import { momentLocal } from 'models/shared/helpers/dates';
import SearchBar from 'components/hocs/searchBar/searchBar';
import {
	TileTracker,
	VehicleList,
} from 'models/geozon/interfaces/tile.interface';
import { fetchTiles } from 'models/geozon/controllers/tile.controller';
import EditDevice from './EditDevice';

const hasVehicleOptions = [
	{
		label: 'Dispositivo asignado',
		value: 'true',
	},
	{
		label: 'Dispositivo sin asignar',
		value: 'false',
	},
];

const typeOptions = [
	{
		label: 'GPS',
		value: true,
	},
	{
		label: 'Tile Tracker',
		value: false,
	},
];

function Devices() {
	const navigate = useNavigate();

	const [loading, setLoading] = useState<boolean>(false);
	const [totalPages, setTotalPages] = useState<number>(0);
	const [currentPage, setCurrentPage] = useState<number>(1);

	const [hasVehicleFilter, setHasVehicleFilter] = useState([]);
	const [typeFilter, setTypeFilter] = useState(true);

	const [devices, setDevices] =
		useState<(Device & { vehicle: VehicleItem })[]>();
	const [tiles, setTiles] =
		useState<(TileTracker & { vehicle: VehicleList })[]>();
	const [openDetails, setOpenDetails] = useState<boolean>(false);
	const [openDetailsDisabled, setOpenDetailsDisabled] =
		useState<boolean>(false);

	const [selectedDevice, setSelectedDevice] = useState<Device>();

	const [count, setCount] = useState(0);
	const [query, setQuery] = useState('');

	useEffect(() => {
		handleEventGoogle(
			'VIEW_Panda_Devices',
			'Panda',
			'page view to the devices list view',
		);
		const information = getDevicesFilter();
		if (information.currentPage) {
			setCurrentPage(information.currentPage);
		}

		if (information.hasVehicleFilter) {
			setHasVehicleFilter(information.readFilter);
		}
		if (information.typeFilter) {
			setTypeFilter(information.typeFilter);
		}
		if (information.query) {
			setQuery(information.query);
		}
	}, []);

	const requestDevices = () => {
		setLoading(true);

		let hasVehicle;
		if (
			hasVehicleFilter.length &&
			hasVehicleFilter.length !== hasVehicleOptions.length
		)
			[hasVehicle] = hasVehicleFilter;

		if (typeFilter) {
			fetchDevices({
				page: currentPage,
				limit: 30,
				hasVehicle,
				query,
			}).then((info) => {
				const { data } = info;
				const finalData = data.map((device) => {
					const vehicle = device.vehiclesList?.filter((omx) => !omx.endedAt)[0];
					return {
						...device,
						vehicle,
					};
				});
				setLoading(false);
				setTiles(undefined);
				setDevices(finalData);
				setTotalPages(info.pages);
				setCount(info.count);
			});
		} else {
			fetchTiles({
				page: currentPage,
				limit: 30,
				hasVehicle,
				query,
			}).then((info) => {
				const { data } = info;
				const finalData = data.map((tile) => {
					const vehicle = tile.vehiclesList?.filter((omx) => !omx.endedAt)[0];
					return {
						...tile,
						vehicle,
					};
				});
				setLoading(false);
				setTiles(finalData);
				setDevices(undefined);
				setTotalPages(info.pages);
				setCount(info.count);
			});
		}
	};
	useEffect(() => {
		requestDevices();
	}, [currentPage]);
	useEffect(() => {
		if (currentPage === 1) {
			requestDevices();
		} else {
			setCurrentPage(1);
		}
	}, [typeFilter, hasVehicleFilter]);

	useEffect(() => {
		setDevicesFilter({
			typeFilter,
			readFilter: hasVehicleFilter,
			currentPage,
			query,
		});
	}, [typeFilter, hasVehicleFilter, currentPage, query]);

	const handleSearch = (event: any) => {
		event.preventDefault();
		requestDevices();
	};

	const handleDetailsVehicle = (e: any, internalId?: string) => {
		e.stopPropagation();
		if (internalId) {
			navigate(`/inventory/${internalId}`);
		}
	};

	const renderTable = useCallback(() => {
		if (typeFilter && devices) {
			return (
				<ListTable
					canSelect={false}
					onRowClick={() => {}}
					data={devices}
					cols={[
						{
							label: '',
							weight: 2,
							render: (row) => (
								<div className="flex_align">
									<Eye
										className="dim_lg cursor_pointer text_primary_200"
										onClick={() => {
											setOpenDetails(true);
											setSelectedDevice(row);
											setOpenDetailsDisabled(true);
										}}
									/>
								</div>
							),
						},
						{
							label: 'Teléfono',
							weight: 4,
							render: (row) => (
								<Typography scale="medium" weight="600" className="text_center">
									{row.phone ?? '--'}
								</Typography>
							),
						},
						{
							label: 'IMEI',
							weight: 4,
							render: (row) => (
								<Typography scale="medium" weight="400" className="text_center">
									{row.imei.substring(9) ?? '--'}
								</Typography>
							),
						},
						{
							label: 'Operador SIM',
							weight: 4,
							render: (row) => (
								<Typography scale="medium" weight="400" className="text_center">
									{row.simBrand ?? '--'}
								</Typography>
							),
						},
						{
							label: 'Marca',
							weight: 4,
							render: (row) => (
								<Typography scale="medium" weight="400" className="text_center">
									{row.brand ?? '--'}
								</Typography>
							),
						},
						{
							label: 'Vehículo',
							weight: 4,
							render: (row) => (
								<Typography
									scale="medium"
									weight="400"
									className={`text_center ${
										row.vehicle?.internalId ? 'text_link cursor_pointer' : ''
									}`}
									onClick={
										row.vehicle?.internalId
											? (e) => {
													handleDetailsVehicle(e, row.vehicle?.internalId);
											  }
											: undefined
									}
								>
									{row.vehicle?.internalId ?? 'Sin asignar'}
								</Typography>
							),
						},
						{
							label: 'Fecha de creacion',
							weight: 4,
							render: (row) => (
								<Typography scale="medium" weight="400" className="text_center">
									{row.createdAt
										? momentLocal(row.createdAt)
												.locale('es')
												.format('DD/MM/YYYY')
										: '--'}
								</Typography>
							),
						},
						{
							label: ' ',
							weight: 2,
							render: (row) => (
								<div className="flex_align">
									<Edit
										className="dim_lg cursor_pointer text_primary_200"
										onClick={() => {
											setOpenDetails(true);
											setSelectedDevice(row);
											setOpenDetailsDisabled(false);
										}}
									/>
								</div>
							),
						},
					]}
				/>
			);
		}
		if (!typeFilter && tiles) {
			return (
				<ListTable
					canSelect={false}
					onRowClick={() => {}}
					data={tiles}
					cols={[
						{
							label: 'UUID',
							weight: 4,
							render: (row) => (
								<Typography scale="medium" weight="600" className="text_center">
									{row.uuid ?? '--'}
								</Typography>
							),
						},
						{
							label: 'Vehículo',
							weight: 4,
							render: (row) => (
								<Typography
									scale="medium"
									weight="400"
									className={`text_center ${
										row.vehicle?.internalId ? 'text_link cursor_pointer' : ''
									}`}
									onClick={
										row.vehicle?.internalId
											? (e) => {
													handleDetailsVehicle(e, row.vehicle?.internalId);
											  }
											: undefined
									}
								>
									{row.vehicle?.internalId ?? 'Sin asignar'}
								</Typography>
							),
						},
						{
							label: 'Fecha de creacion',
							weight: 4,
							render: (row) => (
								<Typography scale="medium" weight="400" className="text_center">
									{row.createdAt
										? momentLocal(row.createdAt)
												.locale('es')
												.format('DD/MM/YYYY')
										: '--'}
								</Typography>
							),
						},
					]}
				/>
			);
		}
		return null;
	}, [typeFilter, devices, tiles]);

	return (
		<>
			<div className="dim_100_per flex_content_col shadow_hard inner_container bg_neutral_400">
				<div className="flex_header">
					<div className="bg_neutral_0 shadow_hard p_y_md p_x_xxxl">
						<div className="display_flex flex_gap_sm flex_align_center">
							<Typography
								scale="heading3"
								weight="600"
								textColor="neutral_900"
								className="m_y_md"
							>
								Dispositivos
							</Typography>
							<SearchBar
								handleSearch={handleSearch}
								query={query}
								setQuery={setQuery}
								className="flex_grow_1"
							/>
						</div>
						<div className="display_flex flex_gap_xl">
							<Select
								fixedPlaceholder
								placeholder="Seleccione Tipo"
								options={typeOptions}
								value={typeFilter}
								onChange={(e: any) => {
									setTypeFilter(e.target.value);
								}}
							/>
							<Select
								fixedPlaceholder
								variant="checkbox"
								placeholder="Seleccione estado"
								options={hasVehicleOptions}
								value={hasVehicleFilter}
								onChange={(e: any) => {
									setHasVehicleFilter(e.target.value);
								}}
							/>
						</div>
					</div>
				</div>
				<div className="flex_body p_x_xxxl overflow_x_hidden p_y_md">
					{loading ? (
						<div className="p_y_xl flex_center">Cargando Dispositivos</div>
					) : (
						renderTable()
					)}
				</div>

				<div className="display_flex flex_align_center flex_justify_between flex_footer bg_neutral_0 p_x_xxxl p_y_md">
					{typeFilter ? (
						<Button
							scale="small"
							icon={<AddCircle />}
							className="w_30_per"
							onClick={() => {
								setOpenDetails(true);
								setSelectedDevice(undefined);
								setOpenDetailsDisabled(false);
							}}
						>
							Añadir Dispositivo
						</Button>
					) : (
						<div />
					)}
					<Pager
						totalPages={totalPages}
						setPage={setCurrentPage}
						page={currentPage}
						count={count}
						className="w_fit"
					/>
				</div>
			</div>
			<EditDevice
				open={openDetails}
				setOpen={(value: any) => {
					setOpenDetails(value);
					setSelectedDevice(undefined);
				}}
				setResult={requestDevices}
				disabled={openDetailsDisabled}
				values={selectedDevice}
			/>
		</>
	);
}

export default Devices;
